'use client';

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type {
    BannerView,
    ConsentCategoryKey,
    ConsentContextValue,
    ConsentService,
    ServiceConsentState,
} from './consent.types';
import {
    buildServiceConsent,
    deriveCategories,
    findUndecidedServices,
    isAcceptingConsent,
    isServiceIdAllowed,
} from './consent-services';
import { consentStore, writeConsent } from './consent-store';
import { hasConsentCookie, writeConsentCookie } from './consent-cookie';

const ConsentContext = createContext<ConsentContextValue | null>(null);

type ConsentProviderProps = {
    children: ReactNode;
    /**
     * The active service catalog, resolved on the server from the CMS settings
     * (`resolveActiveServices`). It must not depend on the current page: the
     * stored decision documents exactly these ids, and anything missing from it
     * re-opens the banner.
     */
    services: ConsentService[];
    /**
     * Whether the request carried the decision cookie. It lets the banner render
     * (or stay away) already on the server instead of after hydration — the
     * hydration gate used to make the banner text the LCP element.
     */
    initialDecided?: boolean;
};

export function ConsentProvider({ children, services, initialDecided = false }: ConsentProviderProps) {
    // Persisted consent lives in localStorage; read it SSR-safely via an external store.
    const {
        hydrated,
        serviceConsent,
        decided: hasDecision,
    } = useSyncExternalStore(consentStore.subscribe, consentStore.getSnapshot, consentStore.getServerSnapshot);

    // Ephemeral UI state.
    const [forcedOpen, setForcedOpen] = useState(false);
    const [view, setView] = useState<BannerView>('compact');

    const consent = useMemo(() => deriveCategories(services, serviceConsent), [services, serviceConsent]);

    // Services introduced after the stored decision was made. They carry no
    // consent, so the banner has to ask again before they may run.
    const undecidedServices = useMemo(
        () => findUndecidedServices(services, serviceConsent),
        [services, serviceConsent],
    );

    const commit = useCallback((selection: ServiceConsentState) => {
        writeConsent(selection, true);
        setForcedOpen(false);
        setView('compact');
    }, []);

    // Blanket decisions write an explicit entry for every service in the catalog,
    // which is what makes a later addition detectable as "never asked".
    const acceptAll = useCallback(() => commit(buildServiceConsent(services, true)), [commit, services]);
    const rejectAll = useCallback(() => commit(buildServiceConsent(services, false)), [commit, services]);
    const save = commit;

    // Grant one service but keep the banner asking for the rest.
    const grantService = useCallback((id: string) => {
        const current = consentStore.getSnapshot();
        if (current.serviceConsent[id] === true) return;

        writeConsent({ ...current.serviceConsent, [id]: true }, current.decided);
    }, []);

    const openSettings = useCallback(() => {
        setView('settings');
        setForcedOpen(true);
    }, []);

    const closeBanner = useCallback(() => {
        setForcedOpen(false);
        setView('compact');
    }, []);

    const isGranted = useCallback((category: ConsentCategoryKey) => consent[category], [consent]);

    const isServiceGranted = useCallback(
        (id: string) => isServiceIdAllowed(id, services, serviceConsent),
        [services, serviceConsent],
    );

    // Visitors who decided before the cookie existed carry it only in
    // localStorage — back-fill it once so the server stops rendering the banner.
    useEffect(() => {
        if (hydrated && hasDecision && !hasConsentCookie()) writeConsentCookie(isAcceptingConsent(serviceConsent));
    }, [hydrated, hasDecision, serviceConsent]);

    // Before hydration the cookie is the only signal available — after it, the
    // stored state takes over. A catalog that grew since the decision only shows
    // up client-side, so that case pops the banner in once, after hydration.
    const isSettled = hasDecision && undecidedServices.length === 0;
    const isBannerVisible = hydrated ? !isSettled || forcedOpen : !initialDecided;

    const value = useMemo<ConsentContextValue>(
        () => ({
            hydrated,
            hasDecision,
            undecidedServices,
            consent,
            serviceConsent,
            isGranted,
            isServiceGranted,
            acceptAll,
            rejectAll,
            save,
            grantService,
            isBannerVisible,
            view,
            setView,
            openSettings,
            closeBanner,
            services,
        }),
        [
            hydrated,
            hasDecision,
            undecidedServices,
            consent,
            serviceConsent,
            isGranted,
            isServiceGranted,
            acceptAll,
            rejectAll,
            save,
            grantService,
            isBannerVisible,
            view,
            openSettings,
            closeBanner,
            services,
        ],
    );

    return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
    const ctx = useContext(ConsentContext);
    if (!ctx) throw new Error('useConsent must be used within ConsentProvider');
    return ctx;
}

/** Reactive boolean — re-renders the consumer whenever the category's consent changes. */
export function useConsentGranted(category: ConsentCategoryKey): boolean {
    return useConsent().consent[category];
}

/**
 * Reactive boolean for one catalog service. This is the check every
 * consent-relevant technology should use: the visitor can switch a single
 * service off while its category stays granted.
 */
export function useServiceConsentGranted(id: string): boolean {
    return useConsent().isServiceGranted(id);
}
